import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

import { apiRequest } from '../utils/apiHelper';
import { showWarning, showError, showSuccess } from '../utils/toastHelper';

function Response() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [orderData, setOrderData] = useState(null);
  const [parseError, setParseError] = useState(false);
  
  // Payment status check states
  const [isCheckingPayment, setIsCheckingPayment] = useState(false);
  const [paymentCheckResult, setPaymentCheckResult] = useState(null);
  const [showPaymentCheckResult, setShowPaymentCheckResult] = useState(false);
  
  const status = searchParams.get('status');
  const message = searchParams.get('message');
  const subscriptionId = searchParams.get('subscriptionId');
  const emailSent = searchParams.get('emailSent') === 'true';
  const emailError = searchParams.get('emailError');

  const toastShown = useRef(false);

  // Safely parse order data
  useEffect(() => {
    const orderParam = searchParams.get('order');
    if (orderParam) {
      try {
        const parsed = JSON.parse(orderParam);
        setOrderData(parsed);
        setParseError(false);
      } catch (error) {
        console.error('Failed to parse order data:', error);
        setOrderData(null);
        setParseError(true);
      }
    } else {
      setOrderData(null);
      setParseError(false);
    }
  }, [searchParams]);

  useEffect(() => {
    // Show email status notification if needed
    if (status === 'success' && !emailSent && !toastShown.current) {
      showWarning('Sipariş onay e-postası gönderilemedi. Lütfen spam klasörünüzü kontrol edin.');
      toastShown.current = true;
    }
  }, [status, emailSent]);

  // Payment status check function
  const handlePaymentStatusCheck = async () => {
    setIsCheckingPayment(true);
    setPaymentCheckResult(null);
    setShowPaymentCheckResult(false);

    try {
      // Build query parameters
      let queryParams = '';
      if (subscriptionId) {
        queryParams = `?subscriptionId=${subscriptionId}`;
      }

      const response = await apiRequest(`/subscriptions/payment-status${queryParams}`);
      
      if (response.success) {
        setPaymentCheckResult(response);
        setShowPaymentCheckResult(true);
        
        // Show appropriate message based on check result
        if (response.paymentCheck?.needsAction) {
          showWarning(response.paymentCheck.message);
        } else {
          showSuccess('Ödeme durumu kontrol edildi');
        }
      } else {
        showError(response.message || 'Ödeme durumu kontrol edilemedi');
      }
    } catch (error) {
      console.error('Payment status check error:', error);
      showError(error.message || 'Ödeme durumu kontrol edilirken bir hata oluştu');
    } finally {
      setIsCheckingPayment(false);
    }
  };

  // Safe navigation function
  const handleSubscriptionNavigation = () => {
    if (user?.id) {
      navigate(`/subscription/user/${user.id}`);
    } else {
      navigate('/products');
    }
  };

  // Validate required parameters for success flow
  const isValidSuccessResponse = () => {
    return status === 'success' && subscriptionId;
  };

  if (status === 'success') {
    // Add validation for success response
    if (!isValidSuccessResponse()) {
      console.error('Invalid success response - missing required parameters', {
        status,
        subscriptionId,
        hasUser: !!user?.id
      });
      
      return (
        <div className="container py-5">
          <div className="row justify-content-center">
            <div className="col-md-6">
              <div className="card shadow-lg border-0">
                <div className="card-body p-5 text-center">
                  <i className="fas fa-exclamation-triangle text-warning mb-4" style={{ fontSize: '4rem' }}></i>
                  <h3 className="mb-4">Ödeme Durumu Belirsiz</h3>
                  <p className="text-muted mb-4">
                    Ödeme sonucu net olarak belirlenemedi. Lütfen abonelik durumunuzu kontrol ediniz.
                  </p>
                                     {/* Payment status check result for invalid response */}
                   {showPaymentCheckResult && paymentCheckResult && (
                     <div className="alert alert-warning mb-4 payment-check-result">
                       <h6 className="alert-heading">
                         <i className="fas fa-exclamation-triangle me-2"></i>
                         Ödeme Durumu Kontrol Sonucu
                       </h6>
                       <p className="mb-2">{paymentCheckResult.paymentCheck?.message}</p>
                       {paymentCheckResult.paymentCheck?.recommendation && (
                         <p className="mb-0 small">
                           <strong>Öneri:</strong> {paymentCheckResult.paymentCheck.recommendation}
                         </p>
                       )}
                       
                       {/* Show Iyzico details if available */}
                       {paymentCheckResult.iyzicoDetails && (
                         <div className="mt-3 pt-3 border-top">
                           <small className="text-muted">
                             <strong>Iyzico Durumu:</strong> {paymentCheckResult.iyzicoDetails.subscriptionStatus} | 
                             <strong> Başarılı Ödemeler:</strong> {paymentCheckResult.iyzicoDetails.paidOrders} | 
                             <strong> Başarısız Ödemeler:</strong> {paymentCheckResult.iyzicoDetails.failedOrders}
                           </small>
                         </div>
                       )}
                     </div>
                   )}

                   <div className="d-grid gap-3">
                     <button 
                       className="btn btn-info"
                       onClick={handlePaymentStatusCheck}
                       disabled={isCheckingPayment}
                     >
                       {isCheckingPayment ? (
                         <>
                           <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                           Kontrol Ediliyor...
                         </>
                       ) : (
                         <>
                           <i className="fas fa-search me-2"></i>
                           Ödeme Durumunu Kontrol Et
                         </>
                       )}
                     </button>
                     <button 
                       className="btn btn-orange"
                       onClick={handleSubscriptionNavigation}
                     >
                       <i className="fas fa-box me-2"></i>
                       Abonelik Durumumu Kontrol Et
                     </button>
                     <button 
                       className="btn btn-outline-secondary"
                       onClick={() => navigate('/products')}
                     >
                       <i className="fas fa-home me-2"></i>
                       Ana Sayfaya Dön
                     </button>
                   </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-md-6">
            <div className="card shadow-lg border-0">
              <div className="card-body p-5 text-center">
                <div className="success-animation mb-4">
                  <i className="fas fa-check-circle text-success" style={{ fontSize: '4rem' }}></i>
                </div>
                <h3 className="mb-4">Aboneliğiniz Başarıyla Oluşturuldu!</h3>
                
                {parseError && (
                  <div className="alert alert-warning mb-4">
                    <p className="mb-2">
                      <strong>Sipariş bilgileri okunamadı. Daha sonra tekrar deneyiniz.</strong>
                    </p>
                    <p className="mb-0 small">
                      Siparişinizin durumunu kontrol etmek için abonelik sayfanızı ziyaret edebilirsiniz.
                    </p>
                  </div>
                )}

                {!parseError && orderData && (
                  <div className="alert alert-info mb-4">
                    <p className="mb-2">
                      <strong>Sipariş Numaranız:</strong> {orderData.orderNumber}
                    </p>
                    <p className="mb-0 small">
                      Siparişinizin durumunu kontrol etmek için abonelik sayfanızı ziyaret edebilirsiniz.
                    </p>
                  </div>
                )}

                {!parseError && !orderData && (
                  <div className="alert alert-info mb-4">
                    <p className="mb-2">
                      <strong>Sipariş bilgileri yüklenemedi.</strong>
                    </p>
                    <p className="mb-0 small">
                      Siparişinizin durumunu kontrol etmek için abonelik sayfanızı ziyaret edebilirsiniz.
                    </p>
                  </div>
                )}

                <div className="alert alert-light border mb-4">
                  <div className="d-flex align-items-start">
                    <i className="fas fa-info-circle text-primary me-2 mt-1"></i>
                    <div className="text-start">
                      <p className="mb-2">Siparişiniz hazırlanmaya başlandı.</p>
                      <p className="mb-0 small">
                        Siparişinizin durumu hakkında e-posta ile bilgilendirileceksiniz.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Payment status check result for success */}
                {showPaymentCheckResult && paymentCheckResult && (
                  <div className="alert alert-success mb-4 payment-check-result">
                    <h6 className="alert-heading">
                      <i className="fas fa-check-circle me-2"></i>
                      Ödeme Durumu Kontrol Sonucu
                    </h6>
                    <p className="mb-2">{paymentCheckResult.paymentCheck?.message}</p>
                    {paymentCheckResult.paymentCheck?.recommendation && (
                      <p className="mb-0 small">
                        <strong>Öneri:</strong> {paymentCheckResult.paymentCheck.recommendation}
                      </p>
                    )}
                    
                    {/* Show Iyzico details if available */}
                    {paymentCheckResult.iyzicoDetails && (
                      <div className="mt-3 pt-3 border-top">
                        <small className="text-muted">
                          <strong>Iyzico Durumu:</strong> {paymentCheckResult.iyzicoDetails.subscriptionStatus} | 
                          <strong> Başarılı Ödemeler:</strong> {paymentCheckResult.iyzicoDetails.paidOrders} | 
                          <strong> Başarısız Ödemeler:</strong> {paymentCheckResult.iyzicoDetails.failedOrders}
                        </small>
                      </div>
                    )}
                  </div>
                )}

                <div className="d-grid gap-3">
                  <button 
                    className="btn btn-orange"
                    onClick={handleSubscriptionNavigation}
                  >
                    <i className="fas fa-box me-2"></i>
                    Aboneliğimi Görüntüle
                  </button>
                  <button 
                    className="btn btn-info"
                    onClick={handlePaymentStatusCheck}
                    disabled={isCheckingPayment}
                  >
                    {isCheckingPayment ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Kontrol Ediliyor...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-search me-2"></i>
                        Ödeme Durumunu Kontrol Et
                      </>
                    )}
                  </button>
                  <button 
                    className="btn btn-outline-secondary"
                    onClick={() => navigate('/products')}
                  >
                    <i className="fas fa-home me-2"></i>
                    Ana Sayfaya Dön
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Error state or unknown status
  const getErrorTitle = () => {
    if (status === 'error') return 'Ödeme İşlemi Başarısız';
    if (!status) return 'Ödeme Durumu Belirsiz';
    return 'Beklenmeyen Durum';
  };

  const getErrorMessage = () => {
    if (message) return message;
    if (status === 'error') return 'Ödeme işlemi sırasında bir hata oluştu';
    if (!status) return 'Ödeme sonucu alınamadı. Lütfen abonelik durumunuzu kontrol ediniz.';
    return `Bilinmeyen durum: ${status}`;
  };

  const getErrorIcon = () => {
    if (status === 'error') return 'fas fa-times-circle text-danger';
    return 'fas fa-exclamation-triangle text-warning';
  };

  return (
    <div className="container py-5">
      <div className="row justify-content-center">
        <div className="col-md-6">
          <div className="card shadow-lg border-0">
            <div className="card-body p-5 text-center">
              <i className={`${getErrorIcon()} mb-4`} style={{ fontSize: '4rem' }}></i>
              <h3 className="mb-4">{getErrorTitle()}</h3>
              <p className="text-muted mb-4">{getErrorMessage()}</p>
              
                             {/* Payment status check result */}
               {showPaymentCheckResult && paymentCheckResult && (
                 <div className="alert alert-info mb-4 payment-check-result">
                   <h6 className="alert-heading">
                     <i className="fas fa-info-circle me-2"></i>
                     Ödeme Durumu Kontrol Sonucu
                   </h6>
                  <p className="mb-2">{paymentCheckResult.paymentCheck?.message}</p>
                  {paymentCheckResult.paymentCheck?.recommendation && (
                    <p className="mb-0 small">
                      <strong>Öneri:</strong> {paymentCheckResult.paymentCheck.recommendation}
                    </p>
                  )}
                  
                  {/* Show Iyzico details if available */}
                  {paymentCheckResult.iyzicoDetails && (
                    <div className="mt-3 pt-3 border-top">
                      <small className="text-muted">
                        <strong>Iyzico Durumu:</strong> {paymentCheckResult.iyzicoDetails.subscriptionStatus} | 
                        <strong> Başarılı Ödemeler:</strong> {paymentCheckResult.iyzicoDetails.paidOrders} | 
                        <strong> Başarısız Ödemeler:</strong> {paymentCheckResult.iyzicoDetails.failedOrders}
                      </small>
                    </div>
                  )}
                </div>
              )}

              <div className="d-grid gap-3">
                <button 
                  className="btn btn-info"
                  onClick={handlePaymentStatusCheck}
                  disabled={isCheckingPayment}
                >
                  {isCheckingPayment ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Kontrol Ediliyor...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-search me-2"></i>
                      Ödeme Durumunu Kontrol Et
                    </>
                  )}
                </button>
                <button 
                  className="btn btn-orange"
                  onClick={() => navigate('/subscription/payment')}
                >
                  <i className="fas fa-redo me-2"></i>
                  Tekrar Dene
                </button>
                <button 
                  className="btn btn-outline-secondary"
                  onClick={() => navigate('/subscription/package')}
                >
                  <i className="fas fa-arrow-left me-2"></i>
                  Pakete Geri Dön
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>
        {`
          .success-animation {
            animation: scaleIn 0.5s ease-in-out;
          }

          @keyframes scaleIn {
            from { transform: scale(0); opacity: 0; }
            to { transform: scale(1); opacity: 1; }
          }

          .btn-info {
            background-color: #17a2b8;
            border-color: #17a2b8;
            transition: all 0.3s ease;
          }

          .btn-info:hover:not(:disabled) {
            background-color: #138496;
            border-color: #117a8b;
            transform: translateY(-1px);
            box-shadow: 0 4px 8px rgba(23, 162, 184, 0.3);
          }

          .btn-info:disabled {
            opacity: 0.7;
            cursor: not-allowed;
            transform: none;
          }

          .alert-heading {
            margin-bottom: 0.75rem;
            font-weight: 600;
          }

          .payment-check-result {
            animation: fadeInUp 0.4s ease-out;
          }

          @keyframes fadeInUp {
            from { 
              opacity: 0;
              transform: translateY(10px);
            }
            to { 
              opacity: 1;
              transform: translateY(0);
            }
          }

          .spinner-border-sm {
            width: 1rem;
            height: 1rem;
          }
        `}
      </style>
    </div>
  );
}

export default Response; 